import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { usePreorder } from '../context/PreorderContext';
import { ShoppingBag, ArrowUpRight, Clock } from 'lucide-react';
import { formatMoney } from '../lib/format';
import { getProductMedia } from '../data/mock';
import WishlistButton from './WishlistButton';

const ProductCard = ({ product, layout = 'default', dark = false }) => {
  const [hovered, setHovered] = useState(false);
  const { addItem, loading } = useCart();
  const { setDialogProduct } = usePreorder();
  const isPreorder = product.preorder === true;
  const discount = product.compareAt
    ? Math.round(((product.compareAt - product.price) / product.compareAt) * 100)
    : 0;

  // Strict media isolation — primary + optional hover from SAME product only.
  const { primary, hover } = getProductMedia(product);
  const showHover = hovered && hover && hover !== primary;

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Preorder / sold-out: no quick-add. The card itself is a Link, so the
    // user will navigate to the PDP where the NotifyMeForm collects email.
    if (isPreorder || !product.availableForSale) {
      return;
    }
    addItem(product, product.sizes?.[0], 1);
  };

  const aspect = layout === 'tall' ? 'aspect-[3/4]' : 'aspect-[4/5]';
  const currency = product.currency || 'AUD';
  // Preorder + in-stock products both route to the same PDP. The PDP itself
  // decides whether to render Add-to-Cart or the NotifyMeForm based on status.
  const linkTo = `/products/${product.handle || product.id}`;

  return (
    <div className="group" data-testid={`product-card-${product.id}`}>
      <Link to={linkTo} className="block">
        <div
          className={`relative ${aspect} overflow-hidden bg-[#EDE3CF]`}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <img
            src={primary}
            alt={product.name}
            loading="lazy"
            decoding="async"
            data-testid={`product-image-primary-${product.id}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[800ms] ${
              showHover ? 'opacity-0' : 'opacity-100'
            }`}
          />
          {hover && (
            <img
              src={hover}
              alt={product.name}
              loading="lazy"
              decoding="async"
              data-testid={`product-image-hover-${product.id}`}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[800ms] ${
                showHover ? 'opacity-100' : 'opacity-0'
              }`}
            />
          )}

          {isPreorder ? (
            <span className="absolute top-4 left-4 bg-[#F5EDDD] text-[#33200F] text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 inline-flex items-center gap-1.5">
              <Clock className="h-3 w-3" strokeWidth={1.5} /> Preorder
            </span>
          ) : (
            discount > 0 && (
              <span className="absolute top-4 left-4 bg-[#33200F] text-[#F5EDDD] text-[10px] tracking-[0.2em] uppercase px-3 py-1.5">
                Save {discount}%
              </span>
            )
          )}

          {!isPreorder && !product.availableForSale && (
            <span className="absolute top-14 right-4 bg-[#F5EDDD] text-[#33200F] text-[10px] tracking-[0.2em] uppercase px-3 py-1.5">
              Sold out
            </span>
          )}

          <div className="absolute top-3 right-3">
            <WishlistButton product={product} size="sm" />
          </div>

          {!isPreorder && product.availableForSale && (
            <button
              onClick={handleAdd}
              disabled={loading}
              className="absolute bottom-4 right-4 bg-[#F5EDDD] text-[#33200F] h-11 w-11 flex items-center justify-center hover:bg-[#33200F] hover:text-[#F5EDDD] transition-colors opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 duration-300 rounded-full disabled:opacity-50"
              aria-label="Add to cart"
              data-testid={`product-card-add-${product.id}`}
            >
              <ShoppingBag className="h-4 w-4" strokeWidth={1.5} />
            </button>
          )}
        </div>

        <div className="mt-5 flex items-start justify-between gap-4">
          <div>
            <h3
              className={`text-[20px] leading-tight ${dark ? 'text-[#F5EDDD]' : 'text-[#33200F]'}`}
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              {product.name}
            </h3>
            {product.tagline && (
              <p className={`text-[12px] italic mt-1 ${dark ? 'text-[#C9A875]' : 'text-[#A07A4F]'}`}>{product.tagline}</p>
            )}
            <div className="flex items-center gap-3 text-[14px] mt-2">
              <span className={dark ? 'text-[#F5EDDD]' : 'text-[#33200F]'}>
                {product.sizePrices ? 'From ' : ''}{formatMoney(product.price, currency)}
              </span>
              {product.compareAt && (
                <span className={`line-through text-[13px] ${dark ? 'text-[#C9A875]' : 'text-[#A07A4F]'}`}>
                  {formatMoney(product.compareAt, currency)}
                </span>
              )}
            </div>
          </div>
          <ArrowUpRight
            className={`h-5 w-5 shrink-0 mt-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform ${dark ? 'text-[#C9A875]' : 'text-[#5B3A24]'}`}
            strokeWidth={1.4}
          />
        </div>
      </Link>
    </div>
  );
};

export default ProductCard;
